/* eslint-disable max-lines-per-function */
import type { Application, Domain } from '@3d4medical/websdk';
import { LanguageType } from '@3d4medical/websdk';
import { ApplicationFactory, TextureSize } from '@3d4medical/websdk';
import { useBridge } from '@completeanatomy/bridge';
/* eslint-disable */
import { useEffect, useState, useRef, useCallback } from 'react';
import { kcGateway } from '../utils/keycloak';
import axios, { AxiosResponse } from 'axios';
import { apiConfig } from '../config/apiConfig';
import type { ScreenResponse } from '../types/ScreenResponse';
import { globalVariables } from '../config/globalVariables';
import { useConfigService } from '@completeanatomy/config';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const isIosDevice = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad/.test(userAgent); // Only iPhone and iPad
};

const defaultDeeplink = 'cDX1Vkbi';

const useApp = () => {
  const [app, setApp] = useState<Application | null>(null);
  const [screen, setScreen] = useState<ScreenResponse | null>(null);
  const bridge = useBridge();
  const config = useConfigService();
  const isCreatingApp = useRef(false);
  const fileCounter = useRef(0);
  const fileCounterLastBunch = useRef(0);
  const timeoutId = useRef<number | null>(null);
  const navigate = useNavigate();
  const [isSetup, setIsSetup] = useState(false);

  const { i18n } = useTranslation();

  useEffect(() => {
    /**
     * Show loading state when app is being created
     */
    if (isCreatingApp.current) {
      bridge?.emit('BridgeObjectChanged', {
        identifier: 'modelState',
        keyPath: '',
        changes: {
          isLoading: true,
        },
      });
    } else {
      bridge?.emit('BridgeObjectChanged', {
        identifier: 'modelState',
        keyPath: '',
        changes: {
          isLoading: false,
        },
      });
    }
  }, [isCreatingApp.current]);

  const createApp = useCallback(async () => {
    let appId = globalVariables.appId;
    if (!appId) {
      console.error('No application id provided');
      isCreatingApp.current = false;

      return;
    }

    let language = i18n.language.split('-')[0] as LanguageType;
    if (!Object.values(LanguageType).includes(language)) {
      i18n.changeLanguage('en');
      language = LanguageType.English;
    }

    const match = window.location.pathname.match(/\/screens\/([^/]+)\/?/);
    const deeplink = match ? match[1] : defaultDeeplink;
    config?.set('OPEN_IN_CA_LINK', `https://3d4medic.al/${deeplink}`);

    const factory = new ApplicationFactory('' as Domain);
    const token = await kcGateway.getToken();
    kcGateway.addConfig(config);

    config?.set('accessToken', token);

    bridge?.emit('BridgeObjectChanged', {
      identifier: 'auth',
      keyPath: '',
      changes: {
        accessToken: token,
        identityToken: await kcGateway.getIdentityToken(),
      },
    });

    const app = await factory.createApplication(
      appId,
      token || '',
      'model-canvas'
    );

    if (!process.env.REACT_APP_KC_TOKEN) {
      let screenData: AxiosResponse<ScreenResponse, any>;
      try {
        screenData = await axios.get<ScreenResponse>(
          apiConfig.screen.replace('{deeplink}', deeplink),
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (
            error.code === 'ECONNABORTED' ||
            error.message.includes('504') // 504 - timeout
          ) {
            navigate('/error');
          }
        }

        return;
      }

      config?.set('applicationId', appId);
      config?.set('identifier', 'identifier');

      const currentScreen = screenData.data;

      bridge?.emit('BridgeObjectChanged', {
        identifier: 'libraryState',
        keyPath: 'openedResources.screen',
        changes: {
          name: currentScreen.name,
          information: currentScreen.description,
          model_identifier: currentScreen.model_id,
          model_version: currentScreen.model_version || 1,
          author: {
            name: currentScreen.user.username,
            image: currentScreen.user.image_url,
          },
        },
      });

      setScreen(currentScreen);
    }

    const textureSize = isIosDevice()
      ? TextureSize.LowGradual
      : TextureSize.MediumGradual;

    app.settings = {
      ...app.settings,
      texture: {
        size: textureSize,
      },
      language: language,
      disableToolkit: true,
      colors: {
        ...app.settings.colors,
        background: {
          r: 17 / 255,
          g: 21 / 255,
          b: 28 / 255,
        },
      },
    };

    setApp(app);
    isCreatingApp.current = false;
  }, [app]);

  const setup = useCallback(async () => {
    const isScreenPage =
      window.location.pathname.includes('/screens/') ||
      window.location.pathname === '/';
    if (!isScreenPage) {
      return;
    }

    const isUserExist = await kcGateway.userManager.getUser();
    if (!isUserExist) {
      navigate('/sign-in');

      return;
    }

    isCreatingApp.current = true;

    config?.set('AWS_SERVER_BACKEND', globalVariables.caDomain);
    config?.set('AWS_SERVER_S3_USERDATA', globalVariables.userData);
    config?.set('SSO_BACKEND', globalVariables.ssoDomain);
    config?.set('MY_ACCOUNT_LINK', globalVariables.userAccount);

    createApp();
  }, [createApp, navigate, config]);

  // set canvas when app is exist
  useEffect(() => {
    if (!app) {
      return;
    }

    const canvas = document.getElementById('model-canvas') as HTMLCanvasElement;
    const canvasContainer = document.getElementById(
      'model-container'
    ) as HTMLElement;

    // Remove unsuded canvas from websdk
    document.getElementById('canvas')?.remove();

    canvasContainer.style.width = '100%';
    canvasContainer.style.height = '100%';
    canvasContainer.style.overflow = 'hidden';
    canvas.tabIndex = -1;

    // Need swap elements, position absolute toolbar brokes everything if it's first child
    canvasContainer.parentElement?.insertBefore(
      canvasContainer,
      canvasContainer.parentElement.firstChild
    );

    // insert canvas to MainScreen component
    canvasContainer.appendChild(canvas);

    app.canvas.setSize(
      canvasContainer.clientWidth,
      canvasContainer.clientHeight
    );

    // Resize the canvas when the container size changes
    /* eslint-disable */
    const observer = new ResizeObserver(() => {
      app.canvas.setSize(
        canvasContainer.clientWidth,
        canvasContainer.clientHeight
      );
    });

    observer.observe(canvasContainer);
    window.dispatchEvent(new Event('resize'));

    setIsSetup(true);
  }, [app]);

  useEffect(() => {
    window.addEventListener('offline', () => {
      navigate('/error');
    });
  });

  useEffect(() => {
    if (isCreatingApp.current || app) {
      return;
    }

    setup();
  }, [app, bridge, config]);

  useEffect(() => {
    if (!app) {
      return;
    }

    app.events.onFailedDownload = () => {
      navigate('/error');
    };

    app.events.onStartedDownload = () => {
      if (timeoutId.current) {
        window.clearTimeout(timeoutId.current);
        timeoutId.current = null;
      }

      bridge?.emit('BridgeObjectChanged', {
        identifier: 'modelState',
        keyPath: '',
        changes: {
          isLoading: true,
        },
      });
    };

    app.events.onCurrentStateDownload = (current, maxSize) => {
      const progress =
        ((current + fileCounterLastBunch.current - 1) /
          (maxSize + fileCounterLastBunch.current)) *
        100;
      fileCounter.current += 1;

      bridge?.emit('BridgeObjectChanged', {
        identifier: 'modelState',
        keyPath: '',
        changes: {
          isLoading: true,
          downloadingProgress: progress,
        },
      });
    };

    app.events.onFinishedDownload = () => {
      fileCounterLastBunch.current += fileCounter.current;
      fileCounter.current = 0;

      timeoutId.current = window.setTimeout(() => {
        bridge?.emit('BridgeObjectChanged', {
          identifier: 'modelState',
          keyPath: '',
          changes: {
            isLoading: false,
            downloadingProgress: 0,
          },
        });

        timeoutId.current = null;
        fileCounterLastBunch.current = 0;
        fileCounter.current = 0;
      }, 500);
    };
  }, [app, bridge]);

  return { app, screen, isSetup };
};

export default useApp;
